import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Award, Check, CircleCheck, Download, FileText, Lock, PencilLine, Play, TriangleAlert } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data as fmtData } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Idioma } from '../../nucleo/formato';
import { novidadePorId, type Novidade } from '../../novidades';
import { Player } from './Player';
import { BASE, BarraProgresso } from './comum';
import { duracaoTexto, relogio, type AulaDetalhe, type AulaItem, type CursoFicha } from './tipos';
import { LinkVoltar } from '../../nucleo/voltar';

/**
 * Fase 2 (W17, K1): aula de texto do curso de novidades. Com a chave de uma novidade do catálogo, o texto, o título e as
 * imagens vêm do catálogo no idioma da pessoa (o banco guarda a versão em pt, que vale para as outras aulas de texto).
 */
function novidadeDaAula(a: Pick<AulaItem, 'tipo' | 'chave_sistema'> | undefined): Novidade | null {
  return a?.tipo === 'texto' && a.chave_sistema ? novidadePorId(a.chave_sistema) : null;
}

interface Conclusao {
  aulas_concluidas: number;
  total_aulas: number;
  curso_concluido: boolean;
}

/** M11.3: área de membros. Vídeo grande com a lista de módulos e aulas ao lado (no celular, a lista vem abaixo). */
export function AreaMembros() {
  const { id = '' } = useParams();
  const { t } = useTranslation('treinamento');
  const { t: tn, i18n } = useTranslation();
  const idioma = (['pt', 'en', 'es'].includes(i18n.language) ? i18n.language : 'pt') as Idioma;
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const [busca, setBusca] = useSearchParams();
  const curso = useQuery({ queryKey: ['treinamento', 'curso', id], queryFn: () => get<CursoFicha>(`/treinamento/cursos/${id}`) });

  const aulaId = busca.get('aula') ?? curso.data?.proxima_aula_id ?? curso.data?.modulos_itens.flatMap((m) => m.aulas)[0]?.id ?? null;
  const aula = useQuery({ queryKey: ['treinamento', 'aula', aulaId], queryFn: () => get<AulaDetalhe>(`/treinamento/aulas/${aulaId}`), enabled: !!aulaId });
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['treinamento', 'curso', id] });
    await qc.invalidateQueries({ queryKey: ['treinamento', 'aula'] });
    await qc.invalidateQueries({ queryKey: ['treinamento', 'cursos'] });
    await qc.invalidateQueries({ queryKey: ['treinamento', 'matriculas'] });
  };

  const matricular = useMutation({
    mutationFn: () => post('/treinamento/matriculas', { curso_id: id }),
    onSuccess: async () => {
      toast.sucesso(t('mensagens.matriculado'));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const concluir = useMutation({
    mutationFn: (aid: string) => post<Conclusao>(`/treinamento/aulas/${aid}/concluir`, {}),
    onSuccess: async (r) => {
      toast.sucesso(r.curso_concluido ? t('mensagens.cursoConcluido') : t('mensagens.aulaConcluida'));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const posicao = useMutation({ mutationFn: (v: { aid: string; seg: number }) => post(`/treinamento/aulas/${v.aid}/posicao`, { posicao_seg: v.seg }) });
  const certificado = useMutation({
    mutationFn: () => post<{ codigo: string }>('/treinamento/certificados', { curso_id: id }),
    onSuccess: async (r) => {
      toast.sucesso(t('mensagens.certificadoEmitido'));
      await recarregar();
      navegar(`${BASE}/certificado/${r.codigo}`);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  if (curso.isPending) return <Carregando />;
  if (curso.isError) return <Erro erro={curso.error} aoTentar={() => curso.refetch()} />;
  const c = curso.data;
  const todas = c.modulos_itens.flatMap((m) => m.aulas);
  const concluidas = todas.filter((a) => a.concluida).length;
  const podeEditar = s.pode('treinamento.editar');
  const escolher = (aid: string) => setBusca({ aula: aid }, { replace: false });
  const a = aula.data;
  const tituloDe = (x: AulaItem | AulaDetalhe) => novidadeDaAula(x)?.textos[idioma].titulo ?? x.titulo;
  const nov = novidadeDaAula(a);
  const txNov = nov?.textos[idioma] ?? null;
  // A aula de texto mostra a primeira imagem da novidade no lugar do player; sem imagem, não há área de mídia.
  const imagemTexto = a?.tipo === 'texto' && !a.bloqueada ? (nov?.imagens?.find((x): x is string => !!x) ?? null) : null;
  const semMidia = a?.tipo === 'texto' && !a.bloqueada && !imagemTexto;
  const temVideo = todas.some((x) => x.tipo !== 'texto');

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <LinkVoltar para={`${BASE}/cursos`} className="inline-flex items-center gap-1 text-sm text-tinta-3 hover:text-tinta-1">
            <ArrowLeft className="size-4" aria-hidden />
            {t('area.voltar')}
          </LinkVoltar>
          <h2 className="mt-1 text-xl font-semibold tracking-tight">{c.titulo}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
            {!c.publicado && <Selo cor="laranja">{t('selos.rascunho')}</Selo>}
            {c.bloqueado && <Selo cor="perigo">{t('selos.bloqueado')}</Selo>}
            {c.minha?.concluida_em && <Selo cor="verde">{t('selos.concluido')}</Selo>}
            <span className="numero">{t('catalogo.aulas', { count: c.aulas })}</span>
            <span className="numero">{duracaoTexto(c.duracao_min)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {podeEditar && (
            <Botao icone={<PencilLine className="size-4" />} onClick={() => navegar(`${BASE}/cursos?editar=${c.id}`)}>
              {t('acoes.editarCurso')}
            </Botao>
          )}
          {c.eu && !c.minha && c.publicado && (
            <Botao variante="primario" carregando={matricular.isPending} onClick={() => matricular.mutate()}>
              {t('acoes.matricularSe')}
            </Botao>
          )}
          {c.minha?.concluida_em && c.minha.codigo_certificado && (
            <Botao icone={<Award className="size-4" />} onClick={() => navegar(`${BASE}/certificado/${c.minha!.codigo_certificado}`)}>
              {t('acoes.verCertificado')}
            </Botao>
          )}
          {c.minha?.concluida_em && !c.minha.codigo_certificado && (
            <Botao variante="primario" icone={<Award className="size-4" />} carregando={certificado.isPending} onClick={() => certificado.mutate()}>
              {t('acoes.emitirCertificado')}
            </Botao>
          )}
        </div>
      </div>

      {!c.eu && <Aviso>{t('area.semFuncionario')}</Aviso>}
      {c.eu && !c.minha && c.publicado && <Aviso>{t('area.semMatricula')}</Aviso>}
      {c.bloqueado && <Aviso perigo>{t('area.cursoBloqueado', { titulo: c.pre_requisito_titulo ?? '' })}</Aviso>}
      {!c.players.conectado && temVideo && (
        <Aviso>
          {t('area.playersDesconectados')}{' '}
          <Link to="/integracoes/integracoes" className="underline">
            {tn('comum.irParaIntegracoes')}
          </Link>
        </Aviso>
      )}

      {todas.length === 0 ? (
        <Cartao>
          <Vazio texto={t('vazio.semAulas')} />
        </Cartao>
      ) : (
        <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            <div className={`relative aspect-video w-full overflow-hidden rounded-xl border border-borda bg-sup-2 ${semMidia ? 'hidden' : ''}`}>
              {aula.isPending && aulaId ? (
                <div className="absolute inset-0">
                  <Carregando />
                </div>
              ) : aula.isError ? (
                <div className="absolute inset-0 overflow-y-auto">
                  <Erro erro={aula.error} aoTentar={() => aula.refetch()} />
                </div>
              ) : a && imagemTexto ? (
                <img key={a.id} src={imagemTexto} alt={tituloDe(a)} data-aula-texto-imagem className="absolute inset-0 size-full object-contain" />
              ) : a && a.embed_url && a.provedor ? (
                <Player
                  key={a.id}
                  provedor={a.provedor}
                  url={a.embed_url}
                  titulo={a.titulo}
                  inicio={a.posicao_seg}
                  aoPosicao={a.registra_progresso ? (seg) => posicao.mutate({ aid: a.id, seg }) : undefined}
                />
              ) : a ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 overflow-y-auto p-6 text-center">
                  {a.bloqueada ? <Lock className="size-6 text-tinta-3" aria-hidden /> : <TriangleAlert className="size-6 text-laranja" aria-hidden />}
                  <p className="max-w-md text-sm text-tinta-2">
                    {a.motivo_bloqueio === 'aula'
                      ? t('area.aulaBloqueada', { titulo: a.pre_requisito_titulo ?? '' })
                      : a.motivo_bloqueio === 'curso'
                        ? t('area.cursoBloqueado', { titulo: c.pre_requisito_titulo ?? '' })
                        : !a.provedor_disponivel
                          ? t('area.provedorIndisponivel', { nome: t(`provedores.${a.provedor}`) })
                          : t('area.semEndereco', { nome: t(`provedores.${a.provedor}`) })}
                  </p>
                  {!a.bloqueada && !a.provedor_disponivel && (
                    <Link to="/integracoes/integracoes" className="text-sm text-acento-forte underline">
                      {tn('comum.irParaIntegracoes')}
                    </Link>
                  )}
                </div>
              ) : null}
            </div>

            {a && (
              <Cartao>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="kicker truncate" title={a.modulo_titulo}>{a.modulo_titulo}</p>
                    <h3 className="mt-1 text-lg font-semibold">{tituloDe(a)}</h3>
                    <p className="mt-1 text-xs text-tinta-3">
                      {a.tipo === 'texto' ? t('area.aulaTexto') : t(`provedores.${a.provedor}`)} <span className="numero">· {duracaoTexto(a.duracao_min)}</span>
                      {a.posicao_seg > 5 && !a.concluida && a.registra_progresso && <span className="numero"> · {t('area.retomar', { tempo: relogio(a.posicao_seg) })}</span>}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Botao tamanho="sm" disabled={!a.anterior_id} icone={<ArrowLeft className="size-4" />} onClick={() => a.anterior_id && escolher(a.anterior_id)}>
                      {t('acoes.anterior')}
                    </Botao>
                    {a.concluida ? (
                      <Selo cor="verde">{t('acoes.concluida')}</Selo>
                    ) : (
                      <Botao
                        tamanho="sm"
                        variante="primario"
                        icone={<Check className="size-4" />}
                        disabled={!a.registra_progresso || a.bloqueada || concluir.isPending}
                        title={!a.registra_progresso ? t('area.semMatricula') : undefined}
                        carregando={concluir.isPending}
                        onClick={() => concluir.mutate(a.id)}
                      >
                        {t('acoes.concluir')}
                      </Botao>
                    )}
                    <Botao tamanho="sm" disabled={!a.proxima_id} onClick={() => a.proxima_id && escolher(a.proxima_id)}>
                      {t('acoes.proxima')}
                      <ArrowRight className="size-4" aria-hidden />
                    </Botao>
                  </div>
                </div>
                {txNov && !a.bloqueada ? (
                  <div data-aula-texto={nov!.id} className="mt-3 flex flex-col gap-3">
                    <p className="text-sm text-tinta-2">{txNov.resumo}</p>
                    <h4 className="kicker">{t('area.passos')}</h4>
                    <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-tinta-1">
                      {txNov.passos.map((p, k) => (
                        <li key={k}>
                          {p}
                          {k > 0 && nov!.imagens?.[k] && <img src={nov!.imagens[k]!} alt={p} loading="lazy" className="mt-2 w-full rounded-lg border border-borda" />}
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : (
                  a.descricao && <p className="mt-3 text-sm whitespace-pre-line text-tinta-2">{a.descricao}</p>
                )}
                <div className="mt-4 border-t border-borda pt-3">
                  <h4 className="kicker mb-2">{t('area.materialApoio')}</h4>
                  {a.materiais.length === 0 ? (
                    <p className="text-sm text-tinta-3">{t('area.semMaterial')}</p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {a.materiais.map((m) => (
                        <li key={m.id}>
                          <a href={`/api/v1/anexos/${m.id}`} download className="inline-flex max-w-full items-center gap-2 text-sm text-acento-forte hover:underline">
                            <FileText className="size-4 shrink-0" aria-hidden />
                            <span className="truncate" title={m.nome}>{m.nome}</span>
                            <Download className="size-3.5 shrink-0" aria-hidden />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Cartao>
            )}
          </div>

          <aside className="min-w-0" aria-label={t('area.conteudo')}>
            <Cartao
              titulo={t('area.conteudo')}
              acao={<span className="numero text-xs text-tinta-3">{t('area.concluidasDe', { feitas: concluidas, count: todas.length })}</span>}
              semPadding
            >
              {c.minha && (
                <div className="border-b border-borda px-4 py-3">
                  <BarraProgresso valor={c.minha.concluida_em ? 100 : Math.round((100 * concluidas) / Math.max(todas.length, 1))} />
                </div>
              )}
              <div className="flex flex-col">
                {c.modulos_itens.map((m) => (
                  <section key={m.id} aria-label={m.titulo} className="border-b border-borda last:border-0">
                    <h3 className="kicker bg-sup-2 px-4 py-2">{m.titulo}</h3>
                    {m.aulas.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-tinta-3">{t('vazio.semAulasModulo')}</p>
                    ) : (
                      <ol>
                        {m.aulas.map((x) => {
                          const ativa = x.id === aulaId;
                          return (
                            <li key={x.id}>
                              <button
                                type="button"
                                onClick={() => escolher(x.id)}
                                aria-current={ativa ? 'true' : undefined}
                                className={`flex w-full min-w-0 items-start gap-3 border-l-2 px-4 py-3 text-left text-sm hover:bg-sup-2 ${ativa ? 'border-acento-forte bg-sup-2' : 'border-transparent'}`}
                              >
                                <span className="mt-0.5 shrink-0" aria-hidden>
                                  {x.concluida ? (
                                    <CircleCheck className="size-4 text-verde" />
                                  ) : x.bloqueada ? (
                                    <Lock className="size-4 text-tinta-3" />
                                  ) : x.tipo === 'texto' ? (
                                    <FileText className={`size-4 ${ativa ? 'text-acento-forte' : 'text-tinta-3'}`} />
                                  ) : (
                                    <Play className={`size-4 ${ativa ? 'text-acento-forte' : 'text-tinta-3'}`} />
                                  )}
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className={`block truncate ${ativa ? 'font-medium text-tinta-1' : 'text-tinta-2'}`} title={tituloDe(x)}>{tituloDe(x)}</span>
                                  <span className="numero block text-xs text-tinta-3">
                                    {duracaoTexto(x.duracao_min)}
                                    {x.materiais > 0 && ` · ${t('area.materiais', { count: x.materiais })}`}
                                  </span>
                                </span>
                                {x.concluida && <span className="sr-only">{t('acoes.concluida')}</span>}
                                {x.bloqueada && <span className="sr-only">{t('selos.bloqueado')}</span>}
                              </button>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </section>
                ))}
              </div>
            </Cartao>
            {c.minha?.concluida_em && <p className="mt-3 text-xs text-tinta-3">{t('area.concluidoEm', { data: fmtData(c.minha.concluida_em) })}</p>}
          </aside>
        </div>
      )}
    </div>
  );
}

function Aviso({ children, perigo }: { children: React.ReactNode; perigo?: boolean }) {
  return (
    <p role="status" className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${perigo ? 'border-perigo text-perigo' : 'border-laranja text-tinta-2'}`}>
      <TriangleAlert className={`mt-0.5 size-4 shrink-0 ${perigo ? '' : 'text-laranja'}`} aria-hidden />
      <span className="min-w-0">{children}</span>
    </p>
  );
}
